// P5 trucks and rounds (RND-C-04): reads from the truck and round views, writes through the round operations
// (ADR-100 to ADR-104). Round cash is null for anyone but the owner and the round's own Seller.
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { supabase } from './supabase';
import type { InventoryRow } from './stock';

export type RoundStatus = 'open' | 'selling_ended' | 'closed';
export interface TruckStockLine {
  product_id: string;
  product: string;
  sku: string | null;
  box_capacity: number | null;
  boxes: number;
  loose: number;
}
export interface Truck {
  id: string;
  code: string;
  category: string;
  vin: string;
  ownership: 'owned' | 'external';
  owner_name: string | null;
  seller_id: string;
  seller_name: string | null;
  active: boolean;
  round_id: string | null;
  round_number: string | null;
  round_status: RoundStatus | null;
  round_started_at: string | null;
  round_cash: string | null;
  stock: TruckStockLine[];
}
export interface SellerChange {
  id: number;
  truck_id: string;
  code: string;
  from_name: string | null;
  to_name: string | null;
  reason: string;
  changed_at: string;
  actor_name: string | null;
}
export interface Round {
  id: string;
  number: string;
  truck_id: string;
  truck_code: string;
  ownership: 'owned' | 'external';
  seller_id: string;
  seller_name: string | null;
  status: RoundStatus;
  started_at: string;
  selling_ended_at: string | null;
  closed_at: string | null;
  cash: string | null;
  reopened: number;
}

function rows<T>(res: { data: unknown; error: { message: string } | null }): T[] {
  if (res.error) throw new Refusal('SERVER_ERROR');
  return (res.data ?? []) as T[];
}

export const ROUNDS = ['rounds'] as const;

export async function loadTrucks(): Promise<Truck[]> {
  return rows<Truck>(await supabase.from('v_trucks').select('*').order('code'));
}
export async function loadSellerChanges(): Promise<SellerChange[]> {
  return rows<SellerChange>(
    await supabase.from('v_seller_changes').select('*').order('changed_at', { ascending: false }),
  );
}
/** The truck's live round (open or selling ended), if any. */
export async function loadLiveRound(truck: string): Promise<Round | null> {
  const list = rows<Round>(
    await supabase.from('v_rounds').select('*').eq('truck_id', truck).neq('status', 'closed').limit(1),
  );
  return list[0] ?? null;
}
/** Every product on one truck, shaped like an Inventory row so the stock sheets and tiles take it as is. */
export async function loadTruckStock(truck: string): Promise<InventoryRow[]> {
  const list = rows<{
    truck_id: string;
    product_id: string;
    name: string;
    sku: string | null;
    box_capacity: number | null;
    active: boolean;
    boxes: number;
    loose: number;
    calc_pieces: number;
  }>(await supabase.from('v_truck_stock').select('*').eq('truck_id', truck).order('name'));
  return list.map((r) => ({ ...r, location_id: r.truck_id, location: 'truck' as const }));
}

export interface MoveLine {
  product: string;
  boxes: number;
  loose: number;
  condition?: 'sellable' | 'damaged';
}
export const saveTruck = (
  id: string | null,
  category: string,
  vin: string,
  ownership: 'owned' | 'external',
  ownerName: string,
  seller: string | null,
) =>
  sendCommand<{ code: string }>('save_truck', {
    p_id: id,
    p_category: category,
    p_vin: vin,
    p_ownership: ownership,
    p_owner_name: ownerName || null,
    p_seller: seller,
  });
export const changeTruckSeller = (truck: string, seller: string, reason: string) =>
  sendCommand('change_truck_seller', { p_truck: truck, p_seller: seller, p_reason: reason });
export const openRound = (truck: string) => sendCommand<{ number: string }>('open_round', { p_truck: truck });
export const endSelling = (round: string) => sendCommand('end_selling', { p_round: round });
export const reopenRound = (round: string, reason: string) =>
  sendCommand('reopen_round', { p_round: round, p_reason: reason });
export const moveStock = (truck: string, direction: 'load' | 'unload', lines: MoveLine[]) =>
  sendCommand('move_stock', { p_truck: truck, p_direction: direction, p_lines: lines });
export const countTruck = (
  truck: string,
  lines: { product: string; boxes: number; loose: number; reason?: string | undefined }[],
) => sendCommand<{ differences: number }>('count_truck', { p_truck: truck, p_lines: lines });
